// The ranked slots editor shared by My Top Movies and Custom Rankings:
// filled rows with rank badges, a ⋮⋮ drag handle, ↑ / ↓ / ✕ buttons (the
// keyboard and screen-reader path), optional ⇄ "replace", and empty slots.
// Extracted from pages/TopMovies — Top Movies renders exactly as before.
import { useCallback, useEffect, useRef, useState } from 'react';
import { targetIndexFor } from '@aluvo/constants/src/topMovies';
import { PosterImage } from '../TopMovies/TopMoviesGrid';
import styles from '../../pages/TopMovies/TopMovies.module.css';

/**
 * Pointer-based drag (mouse, touch and pen through one code path; the native
 * HTML5 drag API doesn't work reliably on phones). Dragging starts from the
 * ⋮⋮ handle only, so swiping elsewhere on a row still scrolls the page. While
 * dragging, the row follows the pointer, the rows it passes slide out of the
 * way, and the page auto-scrolls near the top/bottom edge. The ↑/↓ buttons
 * stay as the keyboard / screen-reader way to reorder.
 */
const EDGE_PX = 72;
const SCROLL_STEP = 14;

const defaultEmptyHint = (index, count) => (index === count ? 'Search for a movie to add it here' : 'Empty');

/**
 * @param {object[]} items        filled slots, in rank order
 * @param {number}   size         total slots (5 or 10)
 * @param {boolean}  [ranked]     rank numbers (else a dot)
 * @param {string}   label        accessible name of the list
 * @param {(item) => string|number} [keyOf]   React key / identity
 * @param {(item) => string} [metaOf]         the line under the title
 * @param {(index, count) => string} [emptyHint]
 * @param {(item, index) => void} onRemove
 * @param {(from, to) => void} onMove
 * @param {(index) => void} [onReplace]       shows a ⇄ button when given
 * @param {number|null} [replacingIndex]      the slot being replaced, highlighted
 */
export default function RankSlots({
    items, size, ranked = true, label,
    keyOf = (item) => item.tmdbId,
    metaOf = (item) => item.year || '',
    emptyHint = defaultEmptyHint,
    onRemove, onMove, onReplace, replacingIndex = null,
}) {
    const rowRefs = useRef([]);
    const dragRef = useRef(null);
    const [drag, setDrag] = useState(null); // { from, to, offset, pitch }
    const slots = Array.from({ length: size }, (_, i) => items[i] || null);
    const count = items.length;

    const update = useCallback(() => {
        const d = dragRef.current;
        if (!d) return;
        const offset = (d.clientY + window.scrollY) - d.startPageY;
        if (!Number.isFinite(offset)) return;
        const to = targetIndexFor(d.from, offset, d.pitch, d.count);
        setDrag({ from: d.from, to, offset, pitch: d.pitch });
    }, []);

    // Auto-scroll while the pointer rests near the viewport edge.
    const tick = useCallback(() => {
        const d = dragRef.current;
        if (!d) return;
        let delta = 0;
        if (d.clientY < EDGE_PX) delta = -SCROLL_STEP;
        else if (d.clientY > window.innerHeight - EDGE_PX) delta = SCROLL_STEP;
        if (delta) {
            // The site sets `scroll-behavior: smooth`; per-frame steps must be instant.
            window.scrollBy({ top: delta, behavior: 'instant' });
            update();
        }
        d.raf = window.requestAnimationFrame(tick);
    }, [update]);

    function startDrag(event, index) {
        if (event.button !== undefined && event.button !== 0) return;
        const rows = rowRefs.current.slice(0, count).filter(Boolean);
        if (rows.length < 2 || !Number.isFinite(event.clientY)) return;
        event.preventDefault();
        // Keep receiving moves when the finger leaves the handle; never fatal.
        try { event.currentTarget.setPointerCapture?.(event.pointerId); } catch { /* unsupported pointer */ }
        const first = rows[0].getBoundingClientRect();
        const second = rows[1].getBoundingClientRect();
        dragRef.current = {
            from: index,
            count,
            pitch: second.top - first.top,
            startPageY: event.clientY + window.scrollY,
            clientY: event.clientY,
            raf: null,
        };
        setDrag({ from: index, to: index, offset: 0, pitch: dragRef.current.pitch });
        dragRef.current.raf = window.requestAnimationFrame(tick);
    }

    function moveDrag(event) {
        if (!dragRef.current || !Number.isFinite(event.clientY)) return;
        dragRef.current.clientY = event.clientY;
        update();
    }

    function endDrag(commit = true) {
        const d = dragRef.current;
        if (!d) return;
        window.cancelAnimationFrame(d.raf);
        dragRef.current = null;
        setDrag((current) => {
            if (commit && current && current.to !== current.from) onMove(current.from, current.to);
            return null;
        });
    }

    useEffect(() => () => { if (dragRef.current) window.cancelAnimationFrame(dragRef.current.raf); }, []);

    // Where each filled row sits while a drag is in progress.
    function shiftFor(index) {
        if (!drag) return 0;
        if (index === drag.from) return drag.offset;
        if (drag.from < drag.to && index > drag.from && index <= drag.to) return -drag.pitch;
        if (drag.from > drag.to && index < drag.from && index >= drag.to) return drag.pitch;
        return 0;
    }
    // Rank badges update live, so the numbers always match where rows will land.
    const displayRank = (index) => {
        if (!drag) return index + 1;
        if (index === drag.from) return drag.to + 1;
        const shift = shiftFor(index);
        if (shift < 0) return index;
        if (shift > 0) return index + 2;
        return index + 1;
    };

    return (
        <ol className={`${styles.slots} ${drag ? styles.slotsDragging : ''}`} aria-label={label}>
            {slots.map((movie, index) => (movie ? (
                <li
                    key={keyOf(movie)}
                    ref={(el) => { rowRefs.current[index] = el; }}
                    className={`${styles.slot} ${drag?.from === index ? styles.slotLifted : ''}${replacingIndex === index ? ` ${styles.slotReplacing}` : ''}`}
                    style={drag ? { transform: `translateY(${shiftFor(index)}px)` } : undefined}
                >
                    <span
                        className={styles.dragHandle}
                        aria-hidden="true"
                        title="Drag to reorder"
                        data-testid={`drag-handle-${index}`}
                        onPointerDown={(e) => startDrag(e, index)}
                        onPointerMove={moveDrag}
                        onPointerUp={() => endDrag(true)}
                        onPointerCancel={() => endDrag(false)}
                        onLostPointerCapture={() => endDrag(true)}
                    >⋮⋮</span>
                    <span className={styles.slotLead}>
                        {ranked ? <span className={styles.slotRank}>{displayRank(index)}</span> : <span className={styles.slotDot} aria-hidden="true" />}
                        <PosterImage movie={movie} className={styles.slotPoster} />
                    </span>
                    <span className={styles.slotText}>
                        <strong>{movie.title}</strong>
                        <span>{metaOf(movie)}</span>
                    </span>
                    <span className={styles.slotActions}>
                        <button type="button" onClick={() => onMove(index, index - 1)} disabled={index === 0} aria-label={`Move ${movie.title} up`}>↑</button>
                        <button type="button" onClick={() => onMove(index, index + 1)} disabled={index === count - 1} aria-label={`Move ${movie.title} down`}>↓</button>
                        {onReplace && (
                            <button type="button" onClick={() => onReplace(index)} aria-label={`Replace ${movie.title}`} aria-pressed={replacingIndex === index}>⇄</button>
                        )}
                        <button type="button" onClick={() => onRemove(movie, index)} aria-label={`Remove ${movie.title}`} className={styles.remove}>✕</button>
                    </span>
                </li>
            ) : (
                <li key={`empty-${index}`} className={`${styles.slot} ${styles.slotEmpty}`}>
                    {ranked ? <span className={styles.slotRank}>{index + 1}</span> : <span className={styles.slotDot} aria-hidden="true" />}
                    <span className={styles.slotPlaceholder}>{emptyHint(index, count)}</span>
                </li>
            )))}
        </ol>
    );
}

